import React, { useState, useEffect, useCallback } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import './Auth.css';
import background1 from './Images/Backgrounds/background1.png';
import { supabase, getDbUserId } from './supabaseClient';

export default function Auth({ onLogin }) {
  const { login, ready, authenticated, user } = usePrivy();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUserAuthenticated = useCallback(() => {
    if (authenticated && user) {
      console.log("User authenticated with Privy:", user);
      
      // Suppress unimportant Privy errors in the console
      const originalConsoleError = console.error;
      console.error = function(...args) {
        if (args[0] && typeof args[0] === 'string') {
          // Filter out specific error messages
          if (args[0].includes('Unable to refresh tokens') || 
              args[0].includes('analytics_events') ||
              args[0].includes('CORS') ||
              args[0].includes('violates row-level security policy')) {
            return;
          }
        }
        originalConsoleError.apply(console, args);
      };
      
      setupUserInDatabase(user).then(() => {
        onLogin();
      });
    }
  }, [authenticated, user, onLogin]);

  useEffect(() => {
    if (ready) {
      handleUserAuthenticated();
    }
  }, [ready, handleUserAuthenticated]);

  const handleLoginClick = () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Attempting login with Privy...");
      login();
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed. Please try again.");
      setIsLoading(false);
    }
  };

  // Function to handle guest login (no authentication)
  const handleGuestLogin = () => {
    console.log("Logging in as guest...");
    // No authentication or database operations for guests
    onLogin(true); // Pass true to indicate guest login
  };

  // Function to get a display name for a user
  const getDisplayName = (user) => {
    if (!user) return 'Anonymous';
    
    if (user.email?.address) {
      return user.email.address.split('@')[0];
    } else if (user.wallet?.address) {
      // Format wallet address to short form
      const address = user.wallet.address;
      return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    }
    
    return 'Anonymous';
  };

  // Function to set up the user in the Supabase database
  const setupUserInDatabase = async (user) => {
    if (!user) return;
    
    try {
      const userId = getDbUserId(user.id);
      console.log("Setting up user in database with ID:", userId);
      
      // Check if user exists in BalloonScores
      const { data: existingData, error: checkError } = await supabase
        .from('BalloonScores')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (checkError && checkError.code !== 'PGRST116') {
        console.log('Notice: Supabase query returned an error:', checkError);
        return;
      }
      
      if (!existingData) {
        // Create initial BalloonScores entry if it doesn't exist
        const displayName = getDisplayName(user);
        
        try {
          const { error: scoreError } = await supabase
            .from('BalloonScores')
            .insert({ 
              user_id: userId,
              email: user.email?.address || '',
              display_name: displayName,
              score: 0,
              all_time_score: 0 
            });
          
          if (scoreError) {
            if (scoreError.code === '42501') { // Permission denied
              console.log('Notice: Row-level security prevented creating a record. Check if the authenticated role has insert permission.');
            } else if (scoreError.code === '23505') { // Unique violation
              console.log('Notice: User record already exists (concurrent creation)');
            } else {
              console.log('Notice: Error creating initial score:', scoreError);
            }
          } else {
            console.log('Created new user profile for', displayName);
          }
        } catch (insertError) {
          console.log('Notice: Could not create user profile:', insertError);
        }
      } else {
        console.log('User already exists in database');
      }
    } catch (dbError) {
      console.log('Notice: Database error:', dbError);
    }
  };

  // Effect to handle user authentication
  useEffect(() => {
    // When authenticated and user object is available
    if (authenticated && user) {
      console.log("User authenticated:", user);
      setupUserInDatabase(user);
      
      // Notify parent that user is authenticated
      handleUserAuthenticated(user);
    }
  }, [authenticated, user, handleUserAuthenticated]);

  if (!ready) {
    return (
      <div className="auth-container" style={{backgroundImage: `url(${background1})`}}>
        <div className="auth-box">
          <h1>Pop Da Pressure</h1>
          <div className="loading-animation"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-panel">
        <h1 className="game-title">Pop Da Pressure</h1>
        <button 
          className="start-button"
          onClick={() => onLogin(true)}
        >
          START
        </button>
      </div>
    </div>
  );
}
